// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import {
  STEP_NAME,
  STEP_NAME_VALUES,
  hasAtMostNDecimals,
  isStepName,
  isValidSowDensityUnit,
  isValidWeightUnit,
} from '@seedplanner/shared-utils';
import { createCSVContent, downloadCSV, getDefaultCrops } from '../../utils';

export const headers = [
  'Name',
  'Yield Per Tray',
  'Yield Per Tray Unit',
  'Grow Time Days',
  'Sow Density Per Tray',
  'Sow Density Unit',
  'Safety Margin',
  'Shelf Life Days',
  'Harvest Buffer Days',
  'Notes',
  'Size 1',
  'Price 1',
  'Size 2',
  'Price 2',
  'Size 3',
  'Price 3',
  'Size 4',
  'Price 4',
  'Step 1 Name',
  'Step 1 Start Day',
  'Step 1 Description',
  'Step 2 Name',
  'Step 2 Start Day',
  'Step 2 Description',
  'Step 3 Name',
  'Step 3 Start Day',
  'Step 3 Description',
  'Step 4 Name',
  'Step 4 Start Day',
  'Step 4 Description',
  'Step 5 Name',
  'Step 5 Start Day',
  'Step 5 Description',
  'Step 6 Name',
  'Step 6 Start Day',
  'Step 6 Description',
];

interface CropStepImport {
  readonly name: string;
  readonly startDay: number;
  readonly description?: string;
}
interface CropSizeImport {
  readonly size: number;
  readonly unit: string;
  readonly price?: number;
}

export interface CropImportRowOut {
  readonly name: string;
  readonly yieldPerTray: number;
  readonly yieldPerTrayUnit: string;
  readonly growTimeDays: number;
  readonly sowDensityPerTray?: number;
  readonly sowDensityUnit?: string;
  readonly bufferPercentage?: number;
  readonly maxHoldTimeDays: number;
  readonly harvestBufferDays?: number;
  readonly notes: string;
  readonly sizes: readonly CropSizeImport[];
  readonly steps: readonly CropStepImport[];
}

export interface CropImportRowError {
  readonly rowIndex: number;
  readonly colHeader: string;
  readonly error: string;
}

interface CropImportRowIn {
  readonly Name: string;
  readonly 'Yield Per Tray': string;
  readonly 'Yield Per Tray Unit': string;
  readonly 'Grow Time Days': string;
  readonly 'Sow Density Per Tray': string;
  readonly 'Sow Density Unit': string;
  readonly 'Safety Margin': string;
  readonly 'Shelf Life Days': string;
  readonly 'Harvest Buffer Days': string;
  readonly Notes: string;
  readonly 'Size 1': string;
  readonly 'Price 1': string;
  readonly 'Size 2': string;
  readonly 'Price 2': string;
  readonly 'Size 3': string;
  readonly 'Price 3': string;
  readonly 'Size 4': string;
  readonly 'Price 4': string;
  readonly 'Step 1 Name': string;
  readonly 'Step 1 Start Day': string;
  readonly 'Step 1 Description': string;
  readonly 'Step 2 Name': string;
  readonly 'Step 2 Start Day': string;
  readonly 'Step 2 Description': string;
  readonly 'Step 3 Name': string;
  readonly 'Step 3 Start Day': string;
  readonly 'Step 3 Description': string;
  readonly 'Step 4 Name': string;
  readonly 'Step 4 Start Day': string;
  readonly 'Step 4 Description': string;
  readonly 'Step 5 Name': string;
  readonly 'Step 5 Start Day': string;
  readonly 'Step 5 Description': string;
  readonly 'Step 6 Name': string;
  readonly 'Step 6 Start Day': string;
  readonly 'Step 6 Description': string;
}

export const validateHeaders = (headersIn: string[]): readonly string[] => {
  // Check that all headers are present and in the correct order
  const resultErrors = [];
  for (const [index, header] of headers.entries()) {
    if (headersIn[index] !== header) {
      resultErrors.push(`Invalid header at column ${index}: ${headersIn[index]}. Expected ${header}`);
    }
  }

  return resultErrors;
};

// Edits to this function should be mirrored in makeCropsCSV.ts
export const validateCropImportRows = (
  rows: readonly CropImportRowIn[],
): {
  readonly resultValues: CropImportRowOut[];
  readonly resultErrors: readonly CropImportRowError[];
  readonly stopRow: number;
} => {
  // For each row, get the values
  // Check that the values are valid
  // Then return parsed values
  // If a row is empty in all columns then stop

  const resultValues: CropImportRowOut[] = [];
  const resultErrors: CropImportRowError[] = [];
  // Header is row 1, so start at row 2
  let rowIndex = 2;
  for (const row of rows) {
    const errorCount = resultErrors.length;

    // If row is empty, then stop
    if (Object.values(row).every((val) => val === '')) {
      break;
    }

    // Name can't be empty
    if (row.Name === '') {
      resultErrors.push({
        rowIndex,
        colHeader: 'Name',
        error: 'Name cannot be empty',
      });
    }
    // Yield per tray can't be empty and must be a valid number
    const yieldPerTray = Number.parseFloat(row['Yield Per Tray']);
    if (row['Yield Per Tray'] === '') {
      resultErrors.push({
        rowIndex,
        colHeader: 'Yield Per Tray',
        error: 'Yield per tray cannot be empty',
      });
    } else if (Number.isNaN(yieldPerTray)) {
      resultErrors.push({
        rowIndex,
        colHeader: 'Yield Per Tray',
        error: 'Yield per tray must be a number',
      });
    } else if (yieldPerTray <= 0) {
      resultErrors.push({
        rowIndex,
        colHeader: 'Yield Per Tray',
        error: 'Yield per tray must be greater than 0',
      });
    }
    // Yield per tray unit can't be empty and must be either 'grams' or 'ounces'
    const yieldPerTrayUnit = row['Yield Per Tray Unit']?.toLowerCase()?.trim();
    if (yieldPerTrayUnit === '' || yieldPerTrayUnit == null) {
      resultErrors.push({
        rowIndex,
        colHeader: 'Yield Per Tray Unit',
        error: 'Yield per tray unit cannot be empty',
      });
    } else if (!isValidWeightUnit(yieldPerTrayUnit)) {
      resultErrors.push({
        rowIndex,
        colHeader: 'Yield Per Tray Unit',
        error: 'Yield per tray unit must be either "grams" or "ounces"',
      });
    }
    // Grow time days can't be empty and must be a valid integer
    const growTimeDays = Number.parseFloat(row['Grow Time Days']);
    if (row['Grow Time Days'] === '') {
      resultErrors.push({
        rowIndex,
        colHeader: 'Grow Time Days',
        error: 'Grow time days cannot be empty',
      });
    } else if (Number.isNaN(growTimeDays)) {
      resultErrors.push({
        rowIndex,
        colHeader: 'Grow Time Days',
        error: 'Grow time days must be an integer',
      });
    } else if (growTimeDays % 1 !== 0) {
      resultErrors.push({
        rowIndex,
        colHeader: 'Grow Time Days',
        error: 'Grow time days must be an integer',
      });
    }
    // Sow density per tray can be empty, but if it's not, it must be a valid number
    let sowDensityPerTray: number | undefined;
    if (row['Sow Density Per Tray'] !== '') {
      sowDensityPerTray = Number.parseFloat(row['Sow Density Per Tray']);
      if (Number.isNaN(sowDensityPerTray)) {
        resultErrors.push({
          rowIndex,
          colHeader: 'Sow Density Per Tray',
          error: 'Sow density per tray must be a number',
        });
      }
    }
    // If sow density per tray is not empty, then sow density unit must be present
    // Sow density unit can be empty, but if it's not, it must be either 'seeds' or 'grams' or 'ounces'
    let sowDensityUnit: string | undefined;
    if (row['Sow Density Per Tray'] !== '') {
      sowDensityUnit = row['Sow Density Unit']?.toLowerCase()?.trim();
      if (sowDensityUnit == null || !isValidSowDensityUnit(sowDensityUnit)) {
        resultErrors.push({
          rowIndex,
          colHeader: 'Sow Density Unit',
          error: 'Sow density unit must be "seeds", "grams", or "ounces"',
        });
      }
    }
    // Safety margin can be empty, but if it's not, it must be a valid integer
    let bufferPercentage: number | undefined;
    if (row['Safety Margin'] !== '') {
      bufferPercentage = Number.parseFloat(row['Safety Margin']);
      if (Number.isNaN(bufferPercentage)) {
        resultErrors.push({
          rowIndex,
          colHeader: 'Safety Margin',
          error: 'Safety margin must be an integer',
        });
      } else if (bufferPercentage < 0 || bufferPercentage > 100) {
        resultErrors.push({
          rowIndex,
          colHeader: 'Safety Margin',
          error: 'Safety margin must be between 0 and 100',
        });
      } else if (bufferPercentage % 1 !== 0) {
        resultErrors.push({
          rowIndex,
          colHeader: 'Safety Margin',
          error: 'Safety margin must be an integer',
        });
      }
    }
    // Shelf Life days can be empty, but if it's not, it must be a valid integer
    let maxHoldTimeDays = 0;
    if (row['Shelf Life Days'] !== '') {
      maxHoldTimeDays = Number.parseFloat(row['Shelf Life Days']);
      if (Number.isNaN(maxHoldTimeDays)) {
        resultErrors.push({
          rowIndex,
          colHeader: 'Shelf Life Days',
          error: 'Shelf life must be an integer number of days',
        });
      } else if (maxHoldTimeDays % 1 !== 0) {
        resultErrors.push({
          rowIndex,
          colHeader: 'Shelf Life Days',
          error: 'Shelf life must be an integer number of days',
        });
      }
    }
    // Harvest buffer days can be empty, but if it's not, it must be a valid integer
    let harvestBufferDays: number | undefined;
    if (row['Harvest Buffer Days'] !== '') {
      harvestBufferDays = Number.parseFloat(row['Harvest Buffer Days']);
      if (Number.isNaN(harvestBufferDays)) {
        resultErrors.push({
          rowIndex,
          colHeader: 'Harvest Buffer Days',
          error: 'Harvest buffer days must be an integer',
        });
      } else if (harvestBufferDays % 1 !== 0) {
        resultErrors.push({
          rowIndex,
          colHeader: 'Harvest Buffer Days',
          error: 'Harvest buffer days must be an integer',
        });
      }
    }
    // Notes can be empty
    const notes = row.Notes;
    // Each size must be in the format of decimal followed by "ounces" or "grams" [WEIGHT_UNITS.GRAMS, WEIGHT_UNITS.OUNCES]
    const sizes: CropSizeImport[] = [];
    for (const [idx, [size, price]] of [
      [row['Size 1'], row['Price 1']],
      [row['Size 2'], row['Price 2']],
      [row['Size 3'], row['Price 3']],
      [row['Size 4'], row['Price 4']],
    ].entries()) {
      const columnNum = idx + 1;
      const sizeColHeader = `Size ${columnNum}`;
      const priceColHeader = `Price ${columnNum}`;

      if (size !== '' && size !== null) {
        const sizeParts = size.split(' ');
        const sizeValue = Number.parseFloat(sizeParts[0]);
        const sizeUnit = sizeParts[1]?.toLowerCase()?.trim();
        const errorMessage =
          'Size must be in the format of a decimal followed by "ounces" or "grams". For example "1.5 ounces" or "2 grams"';
        if (sizeParts.length !== 2) {
          resultErrors.push({
            rowIndex,
            colHeader: sizeColHeader,
            error: errorMessage,
          });
        } else if (Number.isNaN(sizeValue)) {
          resultErrors.push({
            rowIndex,
            colHeader: sizeColHeader,
            error: errorMessage,
          });
        } else if (sizeUnit == null || !isValidWeightUnit(sizeUnit)) {
          resultErrors.push({
            rowIndex,
            colHeader: sizeColHeader,
            error: errorMessage,
          });
        } else if (!hasAtMostNDecimals(sizeValue, 2)) {
          resultErrors.push({
            rowIndex,
            colHeader: sizeColHeader,
            error: 'Size must have at most 2 decimal places',
          });
        } else if (price !== '' && price != null) {
          // Remove any currency symbols from the price
          const priceWithoutSymbol = price.replace(/[^0-9.-]+/g, '');
          const priceValue = Number.parseFloat(priceWithoutSymbol);
          if (Number.isNaN(priceValue)) {
            resultErrors.push({
              rowIndex,
              colHeader: priceColHeader,
              error: 'Price must be a valid number',
            });
          } else if (priceValue <= 0) {
            resultErrors.push({
              rowIndex,
              colHeader: priceColHeader,
              error: 'Price must be greater than 0',
            });
          } else if (!hasAtMostNDecimals(priceValue, 2)) {
            resultErrors.push({
              rowIndex,
              colHeader: priceColHeader,
              error: 'Price must have at most 2 decimal places',
            });
          } else {
            sizes.push({
              size: sizeValue,
              unit: sizeUnit,
              price: priceValue,
            });
          }
        } else {
          sizes.push({
            size: sizeValue,
            unit: sizeUnit,
          });
        }
      }
    }
    // Each step name must be unique and not empty
    // And each step start day must be a valid integer and must be greater than 0 and less than or equal to grow time days
    // And each step description can be empty
    const steps: CropStepImport[] = [];
    const stepKeys = [
      ['Step 1 Name', 'Step 1 Start Day', 'Step 1 Description', 1] as const,
      ['Step 2 Name', 'Step 2 Start Day', 'Step 2 Description', 2] as const,
      ['Step 3 Name', 'Step 3 Start Day', 'Step 3 Description', 3] as const,
      ['Step 4 Name', 'Step 4 Start Day', 'Step 4 Description', 4] as const,
      ['Step 5 Name', 'Step 5 Start Day', 'Step 5 Description', 5] as const,
      ['Step 6 Name', 'Step 6 Start Day', 'Step 6 Description', 6] as const,
    ];
    for (const [name, day, desc, idx] of stepKeys) {
      const stepName = row[name];
      if (stepName === '' || stepName == null) {
        continue;
      }
      // Step name must be one of
      if (!isStepName(stepName)) {
        resultErrors.push({
          rowIndex,
          colHeader: name,
          error: `Step ${idx} name must be one of: ${
            // biome-ignore lint/style/useTemplate: <explanation>
            STEP_NAME_VALUES.map((val) => '`' + val + '`').join(', ')
          }`,
        });
        continue;
      }

      const stepStartDayStr = row[day];
      if (!stepStartDayStr) {
        resultErrors.push({
          rowIndex,
          colHeader: day,
          error: `Step ${idx} start day is required when step name is provided`,
        });
        continue;
      }

      const stepStartDay = Number.parseFloat(stepStartDayStr);
      if (Number.isNaN(stepStartDay)) {
        resultErrors.push({
          rowIndex,
          colHeader: day,
          error: `Step ${idx} start day must be an integer`,
        });
      } else if (stepStartDay % 1 !== 0) {
        resultErrors.push({
          rowIndex,
          colHeader: day,
          error: `Step ${idx} start day must be an integer`,
        });
      } else if (stepStartDay < 0 || stepStartDay > growTimeDays) {
        resultErrors.push({
          rowIndex,
          colHeader: day,
          error: `Step ${idx} start day must be greater than 0 and less than or equal to grow time days`,
        });
      } else {
        const stepDescription = row[desc] || undefined;
        steps.push({
          name: stepName,
          startDay: stepStartDay,
          description: stepDescription,
        });
      }
    }

    // Only add the result if there were no errors
    if (resultErrors.length === errorCount) {
      resultValues.push({
        name: row.Name.trim(),
        yieldPerTray,
        yieldPerTrayUnit,
        growTimeDays,
        sowDensityPerTray,
        sowDensityUnit,
        bufferPercentage,
        maxHoldTimeDays,
        harvestBufferDays,
        notes,
        sizes,
        steps,
      });
    }
    rowIndex += 1;
  }

  return { resultValues, resultErrors, stopRow: rowIndex };
};

export const downloadCropImportTemplate = () => {
  const emptyRow = headers.map(() => '');
  const threeEmptyRows = [emptyRow, emptyRow, emptyRow];
  const instructionRows = instructions.map((instruction) => [instruction]);
  downloadCSV({
    title: 'Microgreen Manager Crop Import Template',
    content: createCSVContent({
      data: [headers, ...threeEmptyRows, ...defaults, ...threeEmptyRows, ...instructionRows],
    }),
  });
};

const instructions = [
  'INSTRUCTIONS:',
  'Do not modify the headers!',
  '',
  'For each row these rules must be followed:',
  '',
  'Name is required',
  'Yield Per Tray is required and must be a number- it can be a decimal',
  'Yield Per Tray Unit is required and must be either `ounces` or `grams`',
  'Grow Time Days is required and must be a number- it cannot be a decimal',
  'Sow Density Per Tray is not required. But if used it must be a number- it can be a decimal',
  'Sow Density Unit is required if Sow Density Per Tray is filled out. It must be one of the following: `grams` or `ounces`',
  'Safety Margin is not required. If used it must be a number- it cannot be a decimal',
  'Shelf Life Days must be a number or left blank- it cannot be a decimal',
  'Harvest Buffer Days must be a number or left blank- it cannot be a decimal',
  'Notes can be anything or can be left blank',
  'Each Size must be in the format of decimal number followed by `ounces` or `grams`',
  'Sizes are optional. You can fill out none, some, or all of them',
  'Each size can have a price associated with it, but it is not required',
  'If a price entered in one of the price columns, it must be a number and cannot be negative. No currency symbols are allowed. Your currency can be set in your account settings',
  `Each step name must be one of the following exactly: ${
    // biome-ignore lint/style/useTemplate: <explanation>
    STEP_NAME_VALUES.map((val) => '`' + val + '`').join(', ')
  }`,
  'Each Step Start Day must be a number- it cannot be a decimal',
  'Any step with an empty Name will be ignored, even if the other step fields are filled out',
  'Each Step Description is optional',
  'Steps are optional. You can fill out none, some, or all of them',
  '',
  'When you upload the file, Microgreen Manager will read the file and do its best to process the data and warn you of any errors',
  'It will read the file from top to bottom, and stop reading when it reaches an empty row',
  'Do not include any double quotes in your data or it will cause an error when processing',
].map(addDoubleQuotes);

const defaults = getDefaultCrops().map((crop) => {
  const [name, sowDensity, ouncesPerTrayYield, holdTimeDays, steps, sizes] = crop;
  const sizesOut = sizes.flatMap((size) => [`${size.size} ${size.unit}`, '']);
  const stepsOut = steps.reduce<string[]>((acc, [stepName, startTime]) => {
    acc.push(...[stepName, startTime.toString(), '']);
    return acc;
  }, []);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const growTime = steps.find(([stepName]) => stepName === STEP_NAME.HARVEST)![1];
  const yieldUnit = 'ounces';
  const sowDensityUnit = 'grams';
  const bufferPercentage = '';
  const harvestBufferDays = '';
  const notes = '';

  // Fill remaining spaces in sizes and steps with empty strings
  for (let i = sizes.length; i < 4; i += 1) {
    sizesOut.push(...['', '']);
  }
  for (let i = steps.length; i < 6; i += 1) {
    stepsOut.push(...['', '', '']);
  }

  // Return in row format
  return [
    name,
    ouncesPerTrayYield.toString(),
    yieldUnit,
    growTime.toString(),
    sowDensity.toString(),
    sowDensityUnit,
    bufferPercentage,
    holdTimeDays.toString(),
    harvestBufferDays,
    notes,
    ...sizesOut,
    ...stepsOut,
  ].map(addDoubleQuotes);
});

export function addDoubleQuotes(str: string) {
  return `"${str}"`;
}
